import { useParams } from "react-router-dom";
import { Box, Typography, Stack, Card, CardContent, Divider } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import useGetSummaryTrial from "../../hooks/useGetSummaryTrial";
import useTrialHierarchy from "../../hooks/useTrialHierarchy";
import Loader from "../../components/Loader";

dayjs.extend(utc);

const TrialHierarchyTab = () => {
  const { _id_ } = useParams();

  const { data: summaryTrial = {}, isLoading: isSummaryTrialLoading } = useGetSummaryTrial({
    trialId: _id_,
  });

  const { data: hierarchyData = {}, isLoading: isHierarchyLoading } = useTrialHierarchy({
    trialId: _id_,
  });

  if (isSummaryTrialLoading || isHierarchyLoading) {
    return <Loader />;
  }

  const cro_user_data = hierarchyData?.cro_user_data || [];
  const specific_role_data = hierarchyData?.specific_role_data || [];
  const reviewers_data = hierarchyData?.reviewers_data || [];

  return (
    <Box>
      {/* CRO Team Structure */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, letterSpacing: 0.2 }}>
          CRO Team Structure
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {cro_user_data.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              py: 2,
              textAlign: "center",
            }}
          >
            No CRO user data available
          </Typography>
        ) : (
          cro_user_data.map((user, index) => (
            <Box key={`cro-user-${index}`} sx={{ mb: 3 }}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  mb: user.Coordinator?.length > 0 ? 2 : 0,
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    CRO Admin
                  </Typography>
                  <Stack spacing={1} sx={{ pl: 0.5 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 4 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Name:
                        </Box>{" "}
                        {user.first_name} {user.last_name}
                      </Typography>

                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Email:
                        </Box>{" "}
                        {user.email}
                      </Typography>

                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Access Status:
                        </Box>{" "}
                        {user.has_access ? "Granted" : "Revoked"}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {user.Coordinator?.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      display: "flex",
                      justifyContent: "center",
                      pt: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 2,
                        bgcolor: "divider",
                        borderRadius: 1,
                        flexGrow: 1,
                      }}
                    />
                  </Box>

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Coordinators
                    </Typography>

                    <Stack spacing={2}>
                      {user.Coordinator.map((coordinator, coordIndex) => (
                        <Card
                          key={`cro-coord-${index}-${coordIndex}`}
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        >
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                              {coordinator.role}
                            </Typography>

                            <Stack spacing={1} sx={{ pl: 0.5 }}>
                              <Stack direction="row" spacing={4}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    Name:
                                  </Box>{" "}
                                  {coordinator.first_name} {coordinator.last_name}
                                </Typography>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    Email:
                                  </Box>{" "}
                                  {coordinator.email}
                                </Typography>

                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    Access Status:
                                  </Box>{" "}
                                  {coordinator.has_access ? "Granted" : "Revoked"}
                                </Typography>
                              </Stack>

                              <Stack direction="row" spacing={4}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    First Access Granted At:
                                  </Box>{" "}
                                  {coordinator.assigned_at
                                    ? dayjs
                                        .utc(coordinator.assigned_at)
                                        .local()
                                        .format("MMM D, YYYY h:mm A")
                                    : "-"}
                                </Typography>

                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    Last Access Revoked At:
                                  </Box>{" "}
                                  {coordinator.revoked_at
                                    ? dayjs
                                        .utc(coordinator.revoked_at)
                                        .local()
                                        .format("MMM D, YYYY h:mm A")
                                    : "-"}
                                </Typography>

                                <Box sx={{ flex: 1 }} />
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                    pl: 0.5,
                  }}
                >
                  No coordinators for this CRO admin
                </Typography>
              )}
            </Box>
          ))
        )}
      </Box>

      {/* Investigator Team Structure */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, letterSpacing: 0.2 }}>
          Investigator Team Structure
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {specific_role_data.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              py: 2,
              textAlign: "center",
            }}
          >
            No investigator data available
          </Typography>
        ) : (
          specific_role_data.map((roleUser, index) => (
            <Box key={`role-user-${index}`} sx={{ mb: 3 }}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  mb: roleUser.Coordinator?.length > 0 ? 2 : 0,
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {roleUser.role} - Site: {String(roleUser.site_id).padStart(2, "0")}
                  </Typography>
                  <Stack spacing={1} sx={{ pl: 0.5 }}>
                    <Stack direction="row" spacing={4}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Name:
                        </Box>{" "}
                        {roleUser.first_name} {roleUser.last_name}
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Email:
                        </Box>{" "}
                        {roleUser.email}
                      </Typography>

                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Access Status:
                        </Box>{" "}
                        {roleUser.has_access ? "Granted" : "Revoked"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={4}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Site Address:
                        </Box>{" "}
                        {roleUser.site_address || "-"}
                      </Typography>

                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Site Phone:
                        </Box>{" "}
                        {roleUser.site_phone || "-"}
                      </Typography>

                      <Box sx={{ flex: 1 }} />
                    </Stack>

                    <Stack direction="row" spacing={4}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          First Access Granted At:
                        </Box>{" "}
                        {roleUser.assigned_at
                          ? dayjs.utc(roleUser.assigned_at).local().format("MMM D, YYYY h:mm A")
                          : "-"}
                      </Typography>

                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Last Access Revoked At:
                        </Box>{" "}
                        {roleUser.revoked_at
                          ? dayjs.utc(roleUser.revoked_at).local().format("MMM D, YYYY h:mm A")
                          : "-"}
                      </Typography>

                      <Box sx={{ flex: 1 }} />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {roleUser.Coordinator?.length > 0 && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box
                    sx={{
                      width: 16,
                      display: "flex",
                      justifyContent: "center",
                      pt: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 2,
                        bgcolor: "divider",
                        borderRadius: 1,
                        flexGrow: 1,
                      }}
                    />
                  </Box>

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Team Members
                    </Typography>

                    <Stack spacing={2}>
                      {roleUser.Coordinator.map((coordinator, coordIndex) => (
                        <Card
                          key={`role-coord-${index}-${coordIndex}`}
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        >
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                              {coordinator.role}
                            </Typography>
                            <Stack spacing={1} sx={{ pl: 0.5 }}>
                              <Stack direction="row" spacing={4}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    Name:
                                  </Box>{" "}
                                  {coordinator.first_name} {coordinator.last_name}
                                </Typography>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    Email:
                                  </Box>{" "}
                                  {coordinator.email}
                                </Typography>

                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    Access Status:
                                  </Box>{" "}
                                  {coordinator.has_access ? "Granted" : "Revoked"}
                                </Typography>
                              </Stack>

                              <Stack direction="row" spacing={4}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    First Access Granted At:
                                  </Box>{" "}
                                  {coordinator.assigned_at
                                    ? dayjs
                                        .utc(coordinator.assigned_at)
                                        .local()
                                        .format("MMM D, YYYY h:mm A")
                                    : "-"}
                                </Typography>

                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    Last Access Revoked At:
                                  </Box>{" "}
                                  {coordinator.revoked_at
                                    ? dayjs
                                        .utc(coordinator.revoked_at)
                                        .local()
                                        .format("MMM D, YYYY h:mm A")
                                    : "-"}
                                </Typography>

                                <Box sx={{ flex: 1 }} />
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}
            </Box>
          ))
        )}
      </Box>

      {/* Reviewers */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, letterSpacing: 0.2 }}>
          Reviewers
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {reviewers_data.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              py: 2,
              textAlign: "center",
            }}
          >
            No reviewers data available
          </Typography>
        ) : (
          reviewers_data.map((roleUser, index) => (
            <Box key={`role-user-${index}`} sx={{ mb: 3 }}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Stack spacing={1} sx={{ pl: 0.5 }}>
                    <Stack direction="row" spacing={4}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Name:
                        </Box>{" "}
                        {roleUser.first_name} {roleUser.last_name}
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Email:
                        </Box>{" "}
                        {roleUser.email}
                      </Typography>

                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Access Status:
                        </Box>{" "}
                        {roleUser.has_access ? "Granted" : "Revoked"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={4}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          First Access Granted At:
                        </Box>{" "}
                        {roleUser.assigned_at
                          ? dayjs.utc(roleUser.assigned_at).local().format("MMM D, YYYY h:mm A")
                          : "-"}
                      </Typography>

                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Last Access Revoked At:
                        </Box>{" "}
                        {roleUser.revoked_at
                          ? dayjs.utc(roleUser.revoked_at).local().format("MMM D, YYYY h:mm A")
                          : "-"}
                      </Typography>

                      <Box sx={{ flex: 1 }} />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default TrialHierarchyTab;
